import React from 'react';
import { Box, Button, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Seo from '../common/Seo';
import { servicePages } from '../../servicePagesData';

const ServicePage = ({ service }) => {
	const relatedServices = servicePages.filter((item) => item.key !== service.key).slice(0, 3);
	const canonicalUrl = `https://brokencosmos.com${service.path}`;
	const serviceJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'Service',
		name: service.kicker,
		description: service.intro,
		provider: {
			'@type': 'Organization',
			name: 'BrokenCosmos Labs',
			legalName: 'Brokencosmos labs Inc.',
			url: 'https://brokencosmos.com',
		},
		areaServed: {
			'@type': 'Country',
			name: 'Canada',
		},
		url: canonicalUrl,
	};

	return (
		<>
			<Seo
				title={service.seoTitle}
				description={service.seoDescription}
				path={service.path}
				jsonLd={serviceJsonLd}
			/>
			<Box sx={{ py: { xs: 10, md: 14 } }}>
				<Container maxWidth="xl">
					<Grid container spacing={{ xs: 4, md: 6 }}>
						<Grid item xs={12} lg={7}>
							<Typography variant="overline" sx={{ color: '#99f6e4', letterSpacing: '0.11em' }}>
								{service.kicker}
							</Typography>
							<Typography variant="h1" component="h1" sx={{ mt: 1.5, mb: 2.5, maxWidth: '14ch', fontSize: 'clamp(2.2rem, 5vw, 4rem)' }}>
								{service.h1}
							</Typography>
							<Typography variant="body1" sx={{ maxWidth: 780, mb: 4 }}>
								{service.intro}
							</Typography>
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
								<Button component={Link} to="/contact" variant="contained">
									Book a Consultation
								</Button>
								<Button component={Link} to="/services" variant="outlined" color="secondary">
									All Services
								</Button>
							</Stack>
						</Grid>

						<Grid item xs={12} lg={5}>
							<Paper
								elevation={0}
								sx={{
									p: 3.5,
									borderRadius: '28px',
									backgroundColor: alpha('#0f172a', 0.72),
								}}
							>
								<Typography variant="h5" sx={{ mb: 2 }}>
									Primary outcomes
								</Typography>
								<Stack spacing={1.5}>
									{service.outcomes.map((outcome) => (
										<Typography key={outcome} variant="body2" sx={{ color: 'text.primary' }}>
											{outcome}
										</Typography>
									))}
								</Stack>
							</Paper>
						</Grid>
					</Grid>

					<Box sx={{ mt: { xs: 6, md: 8 } }}>
						<Typography variant="h2" component="h2" sx={{ mb: 1.5 }}>
							What we typically deliver
						</Typography>
						<Typography variant="body1" sx={{ maxWidth: 760, mb: 4 }}>
							Every engagement is tailored, but this is the typical delivery scope we use to create durable outcomes.
						</Typography>
						<Grid container spacing={2.5}>
							{service.deliverables.map((item) => (
								<Grid item xs={12} md={6} key={item}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											borderRadius: '24px',
											backgroundColor: alpha('#0b1729', 0.68),
										}}
									>
										<Typography variant="h6">{item}</Typography>
									</Paper>
								</Grid>
							))}
						</Grid>
					</Box>

					<Box sx={{ mt: { xs: 7, md: 9 } }}>
						<Typography variant="h3" component="h2" sx={{ mb: 2 }}>
							Related services
						</Typography>
						<Grid container spacing={2.5}>
							{relatedServices.map((item) => (
								<Grid item xs={12} md={4} key={item.key}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											height: '100%',
											borderRadius: '24px',
											backgroundColor: alpha('#0f172a', 0.65),
										}}
									>
										<Typography variant="h6" sx={{ mb: 1.2 }}>
											{item.kicker}
										</Typography>
										<Typography variant="body2" sx={{ mb: 2.5 }}>
											{item.seoDescription}
										</Typography>
										<Button component={Link} to={item.path} variant="outlined" color="secondary">
											View Page
										</Button>
									</Paper>
								</Grid>
							))}
						</Grid>
					</Box>
				</Container>
			</Box>
		</>
	);
};

export default ServicePage;
